import React, { useState } from 'react'
import styled from 'styled-components'
import { Modal } from 'components/common/modal'
import { Column, Row } from 'components/common/layout'
import { H4Text, MediumText, SemiBoldText } from 'components/common/text'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { FiChevronRight } from 'react-icons/fi'
import { useRouter } from 'next/router'
import { RoutePaths } from 'types/router'
import { FileUploader } from 'components/scouts/profile/common/file-uploader'
import { WithIdLink } from 'types/common'
import {
  useIsDesktop,
  useIsDesktopCSR,
  useIsLoggedIn,
  useIsLoggedInGuest,
} from 'utils/hooks'
import {
  ALLOWED_FILE_EXTENSIONS,
  HOST_BASE_URL,
  IS_BROWSER,
} from 'infra/constants'
import { useStores } from 'stores/globals'
import { observer } from 'mobx-react'
import { KakaoLoginBtn } from 'components/misc/login'
import { analytics } from 'infra/analytics'
import { withLoadingCheck } from 'utils/handlers'

export const PositionAnalyzeModal: React.FC = observer(() => {
  if (!IS_BROWSER) return null
  const router = useRouter()
  const isGuest = useIsLoggedInGuest()
  const isDesktop = useIsDesktop()
  const { positionAnalysisStore } = useStores()
  if (!positionAnalysisStore.isOpenAnalysisModal) return null

  return (
    <Modal
      width={positionAnalysisStore.analysisModalStep === 'select' ? 350 : 400}
      onClose={() => positionAnalysisStore.closeAnalysisModal()}
      disableCloseBtn
    >
      <ModalContainer>
        {positionAnalysisStore.analysisModalStep === 'select' ? (
          <AnalyzeEntryOptions
            onClickProfile={() => {
              isDesktop
                ? window.open(RoutePaths.MY_PROFILE, '_blank')
                : router.push(RoutePaths.MY_PROFILE)
              positionAnalysisStore.closeAnalysisModal()
              analytics.track('click_position_analyze_modal_update_profile_btn')
            }}
            onClickAttachment={() => {
              positionAnalysisStore.setAnalysisModalStep('upload')
              analytics.track('click_position_analyze_modal_upload_file_btn')
            }}
          />
        ) : positionAnalysisStore.analysisModalStep === 'upload' ? (
          <AttachmentUploader
            onClickAnalyze={async (files) => {
              if (!positionAnalysisStore.selectedPosition) return
              await positionAnalysisStore.analyze(
                positionAnalysisStore.selectedPosition,
                { fileIds: files.map((file) => file.id) },
                isGuest,
              )
              if (isGuest) positionAnalysisStore.addGuestAnalysisCount()
              isDesktop &&
                window.open(
                  `/positions/${positionAnalysisStore.selectedPosition.id}/analysis`,
                  '_blank',
                )
              positionAnalysisStore.closeAnalysisModal()
            }}
          />
        ) : (
          <Signup />
        )}
      </ModalContainer>
    </Modal>
  )
})

const AttachmentUploader: React.FC<{
  onClickAnalyze: (files: WithIdLink[]) => Promise<void>
}> = ({ onClickAnalyze }) => {
  const isLoggedIn = useIsLoggedIn()
  const isDesktop = useIsDesktopCSR()
  const [files, setFiles] = useState<WithIdLink[]>([])
  const [isFetching, setIsFetching] = useState(false)
  return (
    <Column style={{ gap: 80 }}>
      <Column style={{ gap: 60 }}>
        <Column style={{ gap: 32 }}>
          <MainText>
            내 이력서로
            <br />
            <b>서류 합격 가능성 분석하기</b>
          </MainText>
          <Column style={{ gap: 8 }}>
            <Row style={{ gap: 6 }}>
              <HighlightText>수만 건의 인재 평가 노하우</HighlightText>
              <MediumText>를 바탕으로</MediumText>
            </Row>
            <MediumText>채용 공고 적합도를 분석해드려요</MediumText>
          </Column>
          <Column style={{ gap: 8 }}>
            <Row style={{ gap: 6 }}>
              <HighlightText>경력</HighlightText>
              <HighlightText>기술</HighlightText>
              <HighlightText>컬처핏</HighlightText>
              <MediumText>요소까지 모두 고려해</MediumText>
            </Row>
            <MediumText>꼼꼼하게 분석해드릴게요</MediumText>
          </Column>
        </Column>
        <Column style={{ gap: 24 }}>
          <Column style={{ gap: 12 }}>
            <SemiBoldText>이력서 업로드</SemiBoldText>
            <FileUploader
              files={files}
              onChange={(file) => setFiles([...files, file])}
              onRemove={(id) => setFiles(files.filter((f) => f.id !== id))}
              accept={ALLOWED_FILE_EXTENSIONS}
            />
          </Column>
          <NoticeTextWrapper as='ul'>
            <NoticeText as='li'>
              데이터 추출이 가능한 파일 확장자인지 확인해주세요.
              <br />
              (가능한 확장자: pdf, doc, docx, hwp, hwpx)
            </NoticeText>
            <NoticeText as='li'>
              커리어가 잘 드러나는 이력서, 자기소개서, 경력기술서를 추천해요.
              (포트폴리오는 데이터 추출에 실패할 가능성이 높아요.)
            </NoticeText>
            {isLoggedIn ? (
              <NoticeText as='li'>
                {`파일에서 추출한 데이터는 그룹바이 프로필 구성에 활용됩니다. (‘마이페이지 > ‘내 프로필’에서 확인해주세요.)`}
              </NoticeText>
            ) : (
              <>
                <NoticeText as='li'>
                  이력서에 포함된 개인 정보(실명, 연락처 등)는 사용하지 않으니
                  안심하세요.
                </NoticeText>
                <NoticeText as='li'>
                  파일에서 추출한 데이터는 서류 합격 가능성 분석 외에 사용하지
                  않으며, 추후 회원 가입을 희망하실 경우에 한해 회원 정보 구성에
                  활용합니다.
                </NoticeText>
              </>
            )}
          </NoticeTextWrapper>
        </Column>
      </Column>
      <Button
        variant={files.length > 0 ? BtnType.PRIMARY : BtnType.DISABLE}
        size={isDesktop ? BtnSize.LARGE : BtnSize.MEDIUM}
        onClick={() => {
          if (!files.length) return
          withLoadingCheck(isFetching, setIsFetching, () =>
            onClickAnalyze(files),
          )
        }}
      >
        분석 시작하기
      </Button>
    </Column>
  )
}

const AnalyzeEntryOptions: React.FC<{
  onClickAttachment: () => void
  onClickProfile: () => void
}> = ({ onClickAttachment, onClickProfile }) => {
  return (
    <>
      <MainText>
        서류 합격 가능성을 분석하려면
        <br />
        <b>둘 중 하나는 꼭 필요해요</b>
      </MainText>
      <Column style={{ gap: 12 }}>
        <CustomButton variant={BtnType.WHITE} onClick={onClickProfile}>
          <Row style={{ justifyContent: 'space-between' }}>
            그룹바이 프로필 완성하기
            <FiChevronRight size={24} strokeWidth={1.5} />
          </Row>
        </CustomButton>
        <CustomButton variant={BtnType.WHITE} onClick={onClickAttachment}>
          <Row style={{ justifyContent: 'space-between' }}>
            이력서 파일 첨부하기
            <FiChevronRight size={24} strokeWidth={1.5} />
          </Row>
        </CustomButton>
      </Column>
    </>
  )
}

const Signup: React.FC = () => {
  const router = useRouter()
  return (
    <Column style={{ gap: 40 }}>
      <SignupText>
        회원 가입하고
        <br />
        <b>
          더 많은 공고, 합격 가능성까지
          <br />
          무료로 분석해보세요
        </b>
      </SignupText>
      <KakaoLoginBtn redirectUrl={`${HOST_BASE_URL}/${router.asPath}`} />
    </Column>
  )
}

const SignupText = styled(H4Text)`
  line-height: 1.6;
`

const ModalContainer = styled(Column)`
  gap: 32px;
  padding: 32px 24px;
`

const MainText = styled(H4Text)`
  line-height: 1.5;
`

const CustomButton = styled(Button)`
  text-align: start;
  border: 1px solid ${(p) => p.theme.color.line};
`

const HighlightText = styled(SemiBoldText)`
  padding: 2px 4px;
  border-radius: 4px;
  color: ${(p) => p.theme.color.white1};
  background-color: ${(p) => p.theme.color.black1};
`

const NoticeTextWrapper = styled(Column)`
  gap: 8px;
  padding-left: 20px;
`

const NoticeText = styled(MediumText)`
  color: ${(p) => p.theme.color.primary7};
  line-height: 1.5;
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: 14px;
  }
`
